import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import PricingTable from "../components/site/PricingTable";
import { useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import { IMAGES, TARIFS_DEFAUT } from "../lib/constants";
import { Thermometer, Wind, Volume2, CloudFog } from "lucide-react";

const SIGNES = [
  { icone: Thermometer, texte: "L'air soufflé n'est plus vraiment froid, même à fond." },
  { icone: Wind, texte: "Une odeur désagréable apparaît à la mise en route." },
  { icone: Volume2, texte: "Un bruit anormal se fait entendre quand la climatisation tourne." },
  { icone: CloudFog, texte: "La buée met longtemps à disparaître du pare-brise." },
];

export default function Climatisation() {
  const config = useConfig();
  const tarif = config?.tarifs?.climatisation || TARIFS_DEFAUT.climatisation;
  const { openDevis } = useDevis();

  return (
    <>
      <SEO
        title="Recharge climatisation au Cannet dès 50 € | Promo Pneus"
        description="Recharge de climatisation auto au Cannet, près de Cannes : recharge simple dès 50 €, premium dès 80 €. Contrôle complet du circuit, sans rendez-vous."
      />
      <section className="bg-brand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Chapitre 02
            </p>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl md:text-7xl text-white tracking-wide leading-none">
              Climatisation
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-2xl">
              Recharge, contrôle d'étanchéité, test complet du circuit. Air froid l'été, désembuage rapide l'hiver.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingTable titre={tarif.titre} rows={tarif.rows} testid="tarifs-climatisation" />
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div>
                <p className="font-heading font-bold text-6xl text-brand-light leading-none mb-2" aria-hidden="true">01</p>
                <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                  Recharge de climatisation
                </h2>
                <p className="text-brand-body leading-relaxed text-base md:text-lg mb-4">
                  Le fluide frigorigène s'épuise naturellement avec le temps : le système perd en efficacité
                  et le compresseur finit par forcer, au risque d'une casse coûteuse.
                </p>
                <p className="text-brand-body leading-relaxed text-base md:text-lg">
                  Notre intervention comprend l'inspection du circuit, la recharge en fluide adapté à votre
                  véhicule et un test complet de fonctionnement. Une recharge ne se devine pas : elle se mesure.
                </p>
              </div>
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-brand-red" aria-hidden="true" />
                <img src={IMAGES.climStation} alt="Station de recharge de climatisation branchée au moteur, manomètres bleu et rouge visibles"
                  className="w-full h-72 sm:h-96 object-cover relative" loading="lazy" />
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div className="lg:order-2">
                <p className="font-heading font-bold text-6xl text-brand-light leading-none mb-2" aria-hidden="true">02</p>
                <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                  Les avantages
                </h2>
                <p className="text-brand-body leading-relaxed text-base md:text-lg mb-4">
                  Une climatisation entretenue, c'est du confort toute l'année : air frais l'été, désembuage
                  efficace l'hiver. C'est aussi un circuit préservé, qui évite les pannes coûteuses de compresseur.
                </p>
                <p className="text-brand-body leading-relaxed text-base md:text-lg">
                  Un système en bon état consomme moins de carburant qu'un système qui force, et l'air de
                  l'habitacle est filtré : poussière, pollen et allergènes restent dehors.
                </p>
              </div>
              <div className="relative lg:order-1">
                <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-brand-red" aria-hidden="true" />
                <img src={IMAGES.filtre} alt="Remplacement du filtre d'habitacle : filtre encrassé contre filtre neuf"
                  className="w-full h-72 sm:h-96 object-cover relative" loading="lazy" />
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-brand-light p-6 sm:p-10">
              <p className="font-heading font-bold text-6xl text-white leading-none mb-2" aria-hidden="true">03</p>
              <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
                Quand faire recharger sa climatisation
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {SIGNES.map((s, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white p-5">
                    <s.icone size={26} className="text-brand-red shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-brand-dark font-body leading-relaxed">{s.texte}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-brand-body text-lg">
                L'un de ces signes vous parle ? Passez à l'atelier : le contrôle est rapide et sans rendez-vous.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-brand-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
              Pourquoi choisir Promo Pneus pour votre climatisation
            </h2>
            <p className="text-brand-body text-lg leading-relaxed mb-4">
              Nous contrôlons l'étanchéité du circuit avant de recharger. Recharger un circuit qui fuit,
              c'est jeter l'argent par la fenêtre : ici, on vérifie d'abord, on recharge ensuite.
            </p>
            <p className="text-brand-body text-lg leading-relaxed mb-8">
              Diagnostic honnête, tarif affiché, intervention faite devant vous si vous attendez sur place.
              Si le problème n'est pas une simple recharge, on vous le dit et on vous chiffre la réparation avant de toucher à quoi que ce soit.
            </p>
            <button data-testid="clim-devis-bouton" onClick={() => openDevis("Demande concernant : climatisation. ")}
              className="bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
              Demander un devis climatisation
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
